import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DownloadCloud02Icon } from '@/components/ui/icons'
import { PhotosetFinal } from '@/features/photoset/components/photoset-final'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

/**
 * The end-of-post-production screen for a photoset with no host review: the
 * control cohort, and every photoset before the rollout reaches it.
 *
 * This is today's experience and must stay byte-identical to it - the host
 * review contract requires a non-reviewed set to render exactly as it does now,
 * so nothing review-related belongs on this screen.
 */
export const PhotosetCompletedDownload = ({
	urlParams
}: {
	urlParams: PhotosetURLParams
}) => {
	const { t } = useTranslation()

	// Reads the cache rather than fetching: `PhotosetRoute` loads this same list
	// for every photoset screen, so enabling it here would duplicate that request.
	// Carried over from the screen this replaces, which did the same.
	const {
		data: images,
		isPending,
		fetchStatus
	} = useGetPhotosetImagesList(
		{ urlParams: { photosetId: urlParams.id } },
		{ enabled: false }
	)

	// A disabled query reports `fetchStatus: 'idle'`; only the parent's in-flight
	// request should hold the spinner here.
	if (isPending && fetchStatus === 'fetching') {
		return (
			<PhotosetFinal
				isLoading
				subtitle=''
				title={t('photoset:completed.title')}
			/>
		)
	}

	const finalSelectCount =
		images?.filter((image) => image.is_final_select).length ?? 0

	return (
		<PhotosetFinal
			endSlot={
				<div className='flex flex-col items-center gap-3'>
					<ButtonLegacy size='lg' type='button'>
						<DownloadCloud02Icon />
						{t('photoset:completed.download_image', {
							count: finalSelectCount
						})}
					</ButtonLegacy>
				</div>
			}
			status='success'
			subtitle={t('photoset:completed.paragraph')}
			title={t('photoset:completed.title')}
		/>
	)
}

PhotosetCompletedDownload.displayName = 'PhotosetCompletedDownload'
